import React from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Box, Paper, Typography, Button, Alert, CircularProgress, Stack } from '@mui/material';

import { useAuth } from '@frontend/components/AuthProvider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@frontend/components/Layout';
import { BillingTable, BillingLedgerRow } from './BillingTable';
import { BillingPaymentMethodModal } from './BillingPaymentMethodModal';
import { BillingAddFundsModal } from './BillingAddFundsModal';
import { BillingAutoPaymentModal } from './BillingAutoPaymentModal';

interface BillingResponse {
  clientSecret?: string;
  customerId?: string;
  error?: string;
  status?: string;
}

export default function Billing(): JSX.Element {
  const { activeOrganization, refreshSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = React.useState(false);
  const [clientSecret, setClientSecret] = React.useState<string>('');
  const [balance, setBalance] = React.useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isAddFundsModalOpen, setIsAddFundsModalOpen] = React.useState(false);
  const [addFundsAmount, setAddFundsAmount] = React.useState(10);
  const [ledgerRows, setLedgerRows] = React.useState<BillingLedgerRow[]>([]);
  const [isAutoPaymentModalOpen, setIsAutoPaymentModalOpen] = React.useState(false);
  const [paymentMode, setPaymentMode] = React.useState<'manual' | 'auto' | 'invoice'>('manual');
  const [autoPaymentConfig, setAutoPaymentConfig] = React.useState<{
    thresholdAmount: number;
    rechargeAmount: number;
  } | null>(null);

  const fetchBalance = async () => {
    try {
      const response = await fetch('/api/v1/billing/balance');
      if (!response.ok) {
        throw new Error('Failed to fetch balance');
      }
      const data = (await response.json()) as number;
      setBalance(data);
    } catch (error) {
      console.error('Error fetching balance:', error);
      setNotification({ type: 'error', message: 'Failed to load current balance.' });
    }
  };

  const fetchLedger = async () => {
    try {
      const response = await fetch('/api/v1/billing/ledger');
      if (!response.ok) {
        throw new Error('Failed to fetch ledger');
      }
      const data = (await response.json()) as BillingLedgerRow[];
      setLedgerRows(data);
    } catch (error) {
      console.error('Error fetching ledger:', error);
      setNotification({ type: 'error', message: 'Failed to load ledger activity.' });
    }
  };

  const fetchBillingConfig = async () => {
    try {
      const response = await fetch('/api/v1/billing/config');
      if (!response.ok) {
        throw new Error('Failed to fetch billing config');
      }
      const data = (await response.json()) as {
        paymentMode?: 'manual' | 'auto' | 'invoice';
        autoPaymentConfig?: { thresholdAmount: number; rechargeAmount: number };
      };
      setPaymentMode(data.paymentMode || 'manual');
      setAutoPaymentConfig(data.autoPaymentConfig || null);
    } catch (error) {
      console.error('Error fetching billing config:', error);
    }
  };

  const [notification, setNotification] = React.useState<{
    type: 'success' | 'error' | 'info' | 'warning' | null;
    message: string;
    details?: string;
  }>({ type: null, message: '' });

  // Initialize Stripe instance dynamically from backend stripe config
  const [stripePromise, setStripePromise] = React.useState<Promise<Stripe | null> | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/v1/billing/stripe-config');
        if (!res.ok) throw new Error('Failed to load billing page stripe config');
        const data = (await res.json()) as { stripePublicKey?: string };
        if (!data.stripePublicKey) throw new Error('Missing Stripe public key');
        const promise = loadStripe(data.stripePublicKey);
        if (!cancelled) setStripePromise(promise);
      } catch (e) {
        console.error('Failed to initialize Stripe:', e);
        setNotification({ type: 'error', message: 'Failed to initialize billing. Please try again later.' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Show payment method status notification on mount and after updates
  React.useEffect(() => {
    if (!activeOrganization?.hasPaymentMethod) {
      setNotification({
        type: 'info' as const,
        message: 'No Payment Method',
        details: 'Your organization does not have a payment method set up.',
      });
    } else {
      setNotification({ type: null, message: '' });
    }
  }, [activeOrganization?.hasPaymentMethod]);

  // Handle Stripe redirect and refresh session
  React.useEffect(() => {
    const setupIntentId = searchParams.get('setup_intent');
    const setupIntentClientSecret = searchParams.get('setup_intent_client_secret');
    const redirectStatus = searchParams.get('redirect_status');

    const handleRedirect = async () => {
      if (!setupIntentId || !setupIntentClientSecret) return;

      setIsLoading(true);
      try {
        if (redirectStatus === 'succeeded') {
          await refreshSession();
          setNotification({ type: 'success', message: 'Payment method added successfully!' });
        } else {
          setNotification({ type: 'error', message: 'Failed to add payment method. Please try again.' });
        }
      } catch (error) {
        console.error('Error handling redirect:', error);
        setNotification({
          type: 'error',
          message: 'Failed to update billing information. Please contact support.',
        });
      } finally {
        setIsLoading(false);
        // Clean up URL parameters by redirecting to main billing page
        navigate('/billing', { replace: true });
      }
    };

    handleRedirect();
  }, [navigate, refreshSession, searchParams]);

  React.useEffect(() => {
    fetchBalance();
    fetchLedger();
    fetchBillingConfig();
  }, []);

  const handleOpenModal = () => {
    handlePaymentMethod();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setClientSecret('');
  };

  const handleAddFunds = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/billing/add-funds', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount: addFundsAmount }),
      });

      if (!response.ok) {
        const data = (await response.json()) as { error?: string };
        throw new Error(data.error || 'Adding funds failed');
      }

      setNotification({ type: 'success', message: 'Adding funds successful! Balance will update shortly.' });
      fetchBalance();
      fetchLedger();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred.';
      setNotification({ type: 'error', message: errorMessage });
    } finally {
      setIsLoading(false);
      setIsAddFundsModalOpen(false);
    }
  };

  const handleSaveAutoPayment = async (enabled: boolean, threshold: number, recharge: number) => {
    const response = await fetch('/api/v1/billing/auto-payment-config', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ enabled, thresholdAmount: threshold, rechargeAmount: recharge }),
    });

    if (!response.ok) {
      const data = (await response.json()) as { error?: string };
      throw new Error(data.error || 'Failed to save auto-payment settings');
    }

    await fetchBillingConfig();
    setNotification({ type: 'success', message: 'Auto-payment settings updated successfully.' });
  };

  const handlePaymentMethod = async () => {
    try {
      const response = await fetch('/api/v1/billing/payment-method', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = (await response.json()) as BillingResponse;
      if (!response.ok) {
        throw new Error(data.error || 'Failed to setup payment method');
      }

      // Store client secret for Stripe Elements
      if (!data.clientSecret) {
        throw new Error('No client secret returned');
      }
      setClientSecret(data.clientSecret);
    } catch (error) {
      console.error('Error setting up payment:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to setup payment method. Please try again.';
      setNotification({
        type: 'error',
        message: errorMessage,
      });
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to remove your payment method?')) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/billing/payment-method', {
        method: 'DELETE',
      });

      const data = (await response.json()) as BillingResponse;
      if (!response.ok) {
        throw new Error(data.error || 'Failed to remove payment method');
      }

      // Wait for webhook to process
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await refreshSession();
      setClientSecret(''); // Clear client secret when payment method is removed
      setNotification({
        type: 'success',
        message: 'Payment method removed successfully.',
      });
    } catch (error) {
      console.error('Error removing payment method:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel subscription. Please try again.';
      setNotification({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Layout title="Billing">
        <Box>
          <BillingPaymentMethodModal
            open={isModalOpen}
            onClose={handleCloseModal}
            clientSecret={clientSecret}
            stripePromise={stripePromise}
            hasPaymentMethod={activeOrganization?.hasPaymentMethod || false}
            organizationName={activeOrganization?.name}
          />

          {notification.type && (
            <Alert
              severity={
                notification.type === 'success'
                  ? 'success'
                  : notification.type === 'error'
                    ? 'error'
                    : notification.type === 'warning'
                      ? 'warning'
                      : 'info'
              }
              sx={{ mb: 3 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {notification.message}
              </Typography>
              {notification.details && (
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                  {notification.details}
                </Typography>
              )}
            </Alert>
          )}

          {isLoading ? (
            <Paper sx={{ p: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={32} />
              <Typography sx={{ ml: 2, color: 'text.secondary' }}>Processing...</Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Paper sx={{ p: 3 }}>
                {!activeOrganization?.hasPaymentMethod ? (
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Setup Payment Method
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your organization doesn't have a payment method set up. Add a payment method to access premium features.
                    </Typography>
                    <Button onClick={handleOpenModal} variant="contained" fullWidth disabled={isLoading} sx={{ mt: 2 }}>
                      {isLoading ? 'Loading...' : 'Add Payment Method'}
                    </Button>
                  </Stack>
                ) : (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          Payment Method
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Status:{' '}
                          <Typography component="span" sx={{ fontWeight: 500, color: 'success.main' }}>
                            Active
                          </Typography>
                        </Typography>
                      </Box>
                      <Stack spacing={1}>
                        <Button onClick={handleOpenModal} variant="contained" disabled={isLoading} size="small">
                          Update Payment Method
                        </Button>
                        <Button onClick={handleCancelSubscription} variant="outlined" color="error" disabled={isLoading} size="small">
                          Remove Payment Method
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                )}
              </Paper>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {paymentMode === 'invoice' ? 'Since Last Invoice' : 'Current Balance'}
                  </Typography>
                  {paymentMode !== 'invoice' && (
                    <Button
                      onClick={() => setIsAddFundsModalOpen(true)}
                      variant="contained"
                      color="success"
                      disabled={!activeOrganization?.hasPaymentMethod || isLoading}
                    >
                      Add funds
                    </Button>
                  )}
                </Box>
                {balance !== null ? (
                  <>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                      ${paymentMode === 'invoice' ? Math.abs(balance).toFixed(2) : balance.toFixed(2)}
                    </Typography>
                    {(paymentMode === 'manual' || paymentMode === 'auto') && (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Auto-Reload:{' '}
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 500,
                                color: paymentMode === 'auto' && autoPaymentConfig ? 'success.main' : 'text.secondary',
                              }}
                            >
                              {paymentMode === 'auto' && autoPaymentConfig ? 'Enabled' : 'Disabled'}
                            </Typography>
                          </Typography>
                          {paymentMode === 'auto' && autoPaymentConfig && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Threshold: ${autoPaymentConfig.thresholdAmount.toFixed(2)} • Recharge: $
                              {autoPaymentConfig.rechargeAmount.toFixed(2)}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          onClick={() => setIsAutoPaymentModalOpen(true)}
                          variant="outlined"
                          size="small"
                          disabled={!activeOrganization?.hasPaymentMethod || isLoading}
                        >
                          Configure
                        </Button>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={32} />
                    <Typography sx={{ ml: 2, color: 'text.secondary' }}>Loading...</Typography>
                  </Box>
                )}
              </Paper>
              <BillingTable ledgerRows={ledgerRows} />
            </Box>
          )}
        </Box>
      </Layout>
      <BillingAddFundsModal
        open={isAddFundsModalOpen}
        onClose={() => setIsAddFundsModalOpen(false)}
        isLoading={isLoading}
        addFundsAmount={addFundsAmount}
        setAddFundsAmount={setAddFundsAmount}
        handleAddFunds={handleAddFunds}
      />
      <BillingAutoPaymentModal
        open={isAutoPaymentModalOpen}
        onClose={() => setIsAutoPaymentModalOpen(false)}
        isLoading={isLoading}
        autoPaymentEnabled={paymentMode === 'auto' && !!autoPaymentConfig}
        thresholdAmount={autoPaymentConfig?.thresholdAmount || 20}
        rechargeAmount={autoPaymentConfig?.rechargeAmount || 10}
        onSave={handleSaveAutoPayment}
      />
    </>
  );
}
